<template>
  <AuthLayout
    title="Set Up Your Account"
    description="Create a password to complete your account setup."
  >
    <div>
      <!-- Success Message -->
      <div v-if="success" class="p-4 mb-4 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <CheckIcon class="w-5 h-5 text-green-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-green-800">Password Set Successfully!</h3>
            <div class="mt-2 text-sm text-green-700">
              <p>Your password has been set successfully. You can now log in to your account.</p>
            </div>
            <div class="mt-4">
              <div class="flex space-x-3">
                <BaseButton
                  text="Go to Login"
                  variant="primary"
                  size="sm"
                  @click="$router.push('/')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <OctagonExclamationIcon class="w-5 h-5 text-red-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error Setting Password</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>{{ error }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-sm text-gray-600">Setting up your account...</p>
      </div>

      <!-- Token Error -->
      <div v-if="tokenError" class="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <OctagonExclamationIcon class="w-5 h-5 text-red-400" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Invalid or Expired Link</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>The setup link is invalid or has expired. Please request a new invitation.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- User Info (if token is valid) -->
      <div v-if="userInfo && !success && !loading && !tokenError" class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <UserCircleIcon class="w-8 h-8 text-blue-400" />
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-blue-800">Setting up account for:</p>
            <p class="text-sm text-blue-700">{{ userInfo.email }}</p>
            <p class="text-xs text-blue-600 mt-1">Username: {{ userInfo.username }}</p>
          </div>
        </div>
      </div>

      <!-- Setup Form (only show if token is valid and not success) -->
      <form v-if="!success && !loading && !tokenError && userInfo" @submit.prevent="handleSubmit">
        <div class="space-y-5">
          <div>
            <TextInputDefault
              v-model="form.password"
              labelText="Password"
              placeholder="Enter your password"
              id="password"
              name="password"
              type="password"
              :icon="showPassword ? 'eye-slash' : 'eye'"
              @icon-click="showPassword = !showPassword"
              :asterisk="true"
              :errorMessage="errors.password"
              :required="true"
              @update:modelValue="clearError('password')"
            />
            <p class="mt-1 text-xs text-gray-500">Must be at least 6 characters long</p>
          </div>

          <div>
            <TextInputDefault
              v-model="form.confirmPassword"
              labelText="Confirm Password"
              placeholder="Confirm your password"
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              @icon-click="showConfirmPassword = !showConfirmPassword"
              :asterisk="true"
              :errorMessage="errors.confirmPassword"
              :required="true"
              @update:modelValue="clearError('confirmPassword')"
            />
          </div>

          <div class="flex items-center">
            <input
              v-model="form.agreeTerms"
              id="agreeTerms"
              name="agreeTerms"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              required
            />
            <label for="agreeTerms" class="ml-2 block text-sm text-gray-900">
              I agree to the
              <a href="#" class="text-blue-600 hover:text-blue-500">Terms of Service</a>
              and
              <a href="#" class="text-blue-600 hover:text-blue-500">Privacy Policy</a>
            </label>
          </div>

          <BaseButton
            type="submit"
            :text="submitting ? 'Setting Password...' : 'Set Password'"
            variant="primary"
            size="md"
            class="w-full"
            :disabled="submitting || !form.agreeTerms"
            :loading="submitting"
          />
        </div>
      </form>

      <!-- Help Links -->
      <div class="mt-6 text-center text-sm">
        <p class="text-gray-600">
          Having trouble?
          <a href="mailto:support@example.com" class="font-medium text-blue-600 hover:text-blue-500">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AuthLayout from '@/components/layout/AuthLayout.vue';
import TextInputDefault from '@/components/forms/TextInputDefault.vue';
import BaseButton from '@/components/Buttons/BaseButton.vue';
import { UserApiService } from '@/services/userApiService';
import {
  UserCircleIcon,
  CheckIcon,
  OctagonExclamationIcon
} from '@/icons';

const route = useRoute();
const router = useRouter();

// Reactive state
const form = reactive({
  password: '',
  confirmPassword: '',
  agreeTerms: false
});

const errors = reactive({
  password: '',
  confirmPassword: ''
});

const showPassword = ref(false);
const showConfirmPassword = ref(false);
const loading = ref(true);
const submitting = ref(false);
const success = ref(false);
const error = ref('');
const tokenError = ref('');
const userInfo = ref<{
  email: string;
  username: string;
  firstName: string;
} | null>(null);

// Get token from URL
const token = ref('');

// Validate password
const validatePassword = (password: string): boolean => {
  return password.length >= 6;
};

// Validate form
const validateForm = (): boolean => {
  let isValid = true;

  // Clear previous errors
  errors.password = '';
  errors.confirmPassword = '';

  if (!form.password) {
    errors.password = 'Password is required';
    isValid = false;
  } else if (!validatePassword(form.password)) {
    errors.password = 'Password must be at least 6 characters long';
    isValid = false;
  }

  if (!form.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password';
    isValid = false;
  } else if (form.password !== form.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
    isValid = false;
  }

  if (!form.agreeTerms) {
    error.value = 'You must agree to the terms and conditions';
    isValid = false;
  }

  return isValid;
};

// Clear specific error
const clearError = (field: keyof typeof errors) => {
  errors[field] = '';
  error.value = '';
};

// Define error types
interface ApiError {
  message: string;
  status?: number;
  // Add other properties based on your API response
}

// Helper function to extract error message
const getErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  } else if (typeof err === 'object' && err !== null && 'message' in err) {
    const apiError = err as ApiError;
    return apiError.message || 'An unknown error occurred';
  } else if (typeof err === 'string') {
    return err;
  }
  return 'An unknown error occurred';
};

// Helper function to check if error indicates password already set
const isPasswordAlreadySetError = (err: unknown): boolean => {
  const message = getErrorMessage(err).toLowerCase();
  return message.includes('already set') ||
         message.includes('already active') ||
         message.includes('already has password');
};

// Handle form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  submitting.value = true;
  error.value = '';

  try {
    await UserApiService.setupPassword(token.value, form.password);
    success.value = true;

    // Clear form
    form.password = '';
    form.confirmPassword = '';
    form.agreeTerms = false;

    // Auto-redirect to login after 5 seconds
    setTimeout(() => {
      router.push('/');
    }, 5000);

  } catch (err: unknown) {
    const errorMessage = getErrorMessage(err);
    error.value = errorMessage || 'Failed to set password. Please try again.';

    // If error indicates password already set, show different message
    if (isPasswordAlreadySetError(err)) {
      setTimeout(() => {
        router.push('/');
      }, 3000);
    }
  } finally {
    submitting.value = false;
  }
};

// Verify token on component mount
onMounted(async () => {
  const tokenParam = route.query.token as string;

  if (!tokenParam) {
    tokenError.value = 'No setup token found in URL';
    loading.value = false;
    return;
  }

  token.value = tokenParam;

  try {
    // Verify the token with backend
    const info = await UserApiService.verifySetupToken(token.value);
    userInfo.value = info;
  } catch (err: unknown) {
    const errorMessage = getErrorMessage(err);
    tokenError.value = errorMessage || 'Invalid or expired setup link';

    // If error indicates password already set or account active
    if (isPasswordAlreadySetError(err)) {
      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        router.push('/');
      }, 3000);
    }

    console.error('Token verification error:', err);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
/* Add any custom styles here */
</style>
